import { Box, Card, CardContent, CardHeader, Link as MuiLink, Typography } from "@mui/material"
import { Building2, Contact as ContactIcon, Mail, MapPin, Phone } from "lucide-react"
import { useCallback, type JSX } from "react"
import { Link as RouterLink } from "react-router-dom"

import { ResellerNetworkColumn } from "@crm-src/components/reseller-network-column"
import { useResellerPagedList } from "@crm-src/hooks/useResellerPagedList"
import { ContactsAPI, type Contact } from "@crm-src/lib/contacts-api"
import type { Reseller } from "@crm-src/types/lead"

import { ResellerOnboardingCard } from "./ResellerOnboardingCard"
import { ResellerSalespeopleCard } from "./ResellerSalespeopleCard"

/** One "Label: value" line, with "Not provided" as a visibly different state
 * from a real value — a blank looks like a rendering bug. */
// `| undefined` is explicit for `exactOptionalPropertyTypes` — most callers
// pass an optional column that may genuinely be absent.
function Field({ label, value }: { label: string; value?: string | null | undefined }): JSX.Element {
  return (
    <Box>
      <Box component="span" sx={{ fontWeight: 500 }}>{label}: </Box>
      <Box
        component="span"
        sx={value ? { color: "text.secondary" } : { color: "text.disabled", fontStyle: "italic" }}
      >
        {value || "Not provided"}
      </Box>
    </Box>
  )
}

const PANEL_SX = {
  bgcolor: "surfaceSoft",
  borderRadius: 1,
  p: 2,
  fontSize: 14,
  color: "text.secondary",
  display: "flex",
  flexDirection: "column",
  gap: 1,
} as const

/**
 * Overview — who the reseller is, where it is, who looks after it, and who to
 * call there.
 *
 * Company, Address and Assigned Salespeople sit in ONE ROW from `lg` up
 * (2026-09-15; it was Salespeople ∥ Address with Company underneath). They are
 * the three short facts an operator opening a reseller checks first, so they
 * share a line and the same height. Two-up at `md`, stacked below — three
 * narrow columns of address lines on a phone is worse than one readable one.
 *
 * The Salespeople card is where an ADMIN attaches and detaches OUR salespeople
 * (`ResellerSalespeopleCard`); `onChanged` hands the refreshed reseller back
 * to the page so the header and every tab agree.
 */
/** `Contact.id` is a NUMBER and every list column here keys rows by a string
 * id. `Contact & { id: string }` would intersect the two into `never`, so the
 * row type replaces the field rather than adding to it. */
type ContactRow = Omit<Contact, "id"> & { id: string }

export function ResellerOverviewTab({
  reseller,
  onChanged,
}: {
  reseller: Reseller
  /** Re-read the reseller after a salesperson is attached or detached. */
  onChanged: () => void | Promise<void>
}): JSX.Element {
  const contacts = useResellerPagedList<ContactRow>(
    useCallback(
      async ({ page, perPage, search }) => {
        const result = await ContactsAPI.getContacts({
          reseller_id: reseller.id,
          page,
          per_page: perPage,
          ...(search ? { search } : {}),
        })
        const meta = result.meta?.pagination
        return {
          // `Contact.id` is a number and the column keys on a string id.
          rows: (result.data ?? []).map((c) => ({ ...c, id: String(c.id) })),
          totalCount: meta?.total_count ?? result.total_count ?? 0,
          totalPages: meta?.total_pages ?? 1,
        }
      },
      [reseller.id],
    ),
  )

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* The three short panels, in one row. */}
      <Box
        data-cy="reseller-overview-row"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
          gap: 3,
          alignItems: "stretch",
        }}
      >
        <Card data-cy="reseller-company-card" sx={{ height: "100%" }}>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
                <Box sx={{ color: "text.secondary", display: "flex" }}><Building2 size={20} /></Box>
                <span>Company</span>
              </Box>
            }
          />
          <CardContent>
            <Box sx={PANEL_SX}>
              <Field label="Reseller Code" value={reseller.code} />
              <Box>
                <Box component="span" sx={{ fontWeight: 500 }}>Website: </Box>
                {reseller.website ? (
                  <MuiLink href={reseller.website} target="_blank" rel="noopener noreferrer">
                    {reseller.website}
                  </MuiLink>
                ) : (
                  <Box component="span" sx={{ color: "text.disabled", fontStyle: "italic" }}>Not provided</Box>
                )}
              </Box>
              <Box>
                <Box component="span" sx={{ fontWeight: 500 }}>Parent company: </Box>
                {reseller.parent ? (
                  <MuiLink component={RouterLink} to={`/crm/administration/resellers/${reseller.parent.id}`}>
                    {reseller.parent.name}
                    {reseller.parent.code ? ` (${reseller.parent.code})` : ""}
                  </MuiLink>
                ) : (
                  <Box component="span" sx={{ color: "text.disabled", fontStyle: "italic" }}>Top level</Box>
                )}
              </Box>
              <Field label="Legal name" value={reseller.billing?.legal_name} />
              <Field label="Tax ID" value={reseller.billing?.tax_id} />
              <Field label="Billing email" value={reseller.billing?.billing_email} />
              <Field label="Billing phone" value={reseller.billing?.billing_phone} />
            </Box>
          </CardContent>
        </Card>

        <Card data-cy="reseller-address-card" sx={{ height: "100%" }}>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "1.125rem", fontWeight: 600 }}>
                <Box sx={{ color: "text.secondary", display: "flex" }}><MapPin size={20} /></Box>
                <span>Address Information</span>
              </Box>
            }
          />
          <CardContent>
            <Box sx={PANEL_SX}>
              <Field label="Street Address" value={reseller.address} />
              <Field label="City" value={reseller.city} />
              <Field label="State/Province" value={reseller.state} />
              <Field label="Postal Code" value={reseller.postal_code} />
              <Field label="Country" value={reseller.country} />
            </Box>
          </CardContent>
        </Card>

        <ResellerSalespeopleCard reseller={reseller} onChanged={onChanged} />
      </Box>

      {/* How this reseller came to exist. Renders nothing when it was created
          by staff rather than through self-registration. */}
      <ResellerOnboardingCard resellerId={Number(reseller.id)} />

      {/* A real searched + paged list, not a preview: a reseller can hold
          hundreds of contacts, and "who do I call there" is a lookup. */}
      <ResellerNetworkColumn<ContactRow>
        data-cy="reseller-contacts-panel"
        title="Contacts"
        icon={<ContactIcon size={18} />}
        entityNoun="contacts"
        emptyMessage="No contacts recorded for this reseller"
        list={contacts}
        renderRow={(contact) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              p: 1.5,
              bgcolor: "surfaceSoft",
              borderRadius: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {contact.full_name || contact.display_name}
              </Typography>
              {(contact.job_title || contact.company) && (
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}>
                  {[contact.job_title, contact.company].filter(Boolean).join(" · ")}
                </Typography>
              )}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 0.5, fontSize: 12, color: "text.secondary" }}>
                {contact.email && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
                    <Mail size={12} />
                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {contact.email}
                    </Box>
                  </Box>
                )}
                {(contact.phone || contact.mobile) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Phone size={12} />
                    <span>{contact.phone || contact.mobile}</span>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      />
    </Box>
  )
}
